import { useEffect, useRef } from "react";
import { Icon } from "@/components/Icon";
import { useTranslation } from "react-i18next";

interface MessageGesturesProps {
    onReply: () => void;
    disabled: boolean;
}

export const MessageGestures = ({
    onReply,
    disabled,
}: MessageGesturesProps) => {
    const ref = useRef<HTMLSpanElement>(null);
    const { t } = useTranslation("global");
    useEffect(() => {
        const group = ref.current?.closest(
            ".chat-message-group"
        ) as HTMLElement | null;
        const bubble = ref.current?.parentElement;
        if (!bubble || !group || disabled) {
            return;
        }
        let start: { x: number; y: number } | null = null;
        let distance = 0;
        const reset = (): void => {
            start = null;
            distance = 0;
            bubble.removeAttribute("data-swipe-reply");
            group.removeAttribute("data-actions-open");
        };
        const begin = (event: TouchEvent): void => {
            if (
                event.touches.length !== 1 ||
                (event.target as Element).closest("button, a, input, textarea")
            ) {
                return;
            }
            start = {
                x: event.touches[0].clientX,
                y: event.touches[0].clientY,
            };
            distance = 0;
        };
        const move = (event: TouchEvent): void => {
            if (!start) {
                return;
            }
            const dx = event.touches[0].clientX - start.x;
            const dy = event.touches[0].clientY - start.y;
            if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 10) {
                reset();
                return;
            }
            if (Math.abs(dx) < 12) {
                return;
            }
            if (window.getSelection()?.toString()) {
                reset();
                return;
            }
            event.preventDefault();
            distance = dx;
            bubble.toggleAttribute("data-swipe-reply", dx > 30);
            group.toggleAttribute("data-actions-open", dx < -30);
        };
        const end = (): void => {
            if (distance > 65) {
                onReply();
            }
            reset();
        };
        const dismiss = (event: Event): void => {
            if (!group.contains(event.target as Node)) {
                group.removeAttribute("data-actions-open");
            }
        };
        bubble.addEventListener("touchstart", begin, { passive: true });
        bubble.addEventListener("touchmove", move, { passive: false });
        bubble.addEventListener("touchend", end);
        bubble.addEventListener("touchcancel", reset);
        document.addEventListener("pointerdown", dismiss);
        return () => {
            bubble.removeEventListener("touchstart", begin);
            bubble.removeEventListener("touchmove", move);
            bubble.removeEventListener("touchend", end);
            bubble.removeEventListener("touchcancel", reset);
            document.removeEventListener("pointerdown", dismiss);
        };
    }, [onReply, disabled]);
    return (
        <span ref={ref} className="contents">
            <span
                className="chat-swipe-reply absolute bottom-1 left-1 rounded-full bg-white dark:bg-zinc-800 text-primary p-2 shadow-md pointer-events-none"
                aria-label={t("developments.detail.quote_message")}
            >
                <Icon value="reply" size={18} />
            </span>
        </span>
    );
};
